import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";

const err = (e) => toast.error(e.response?.data?.detail || "Something went wrong");
const STATE = { overdue: "text-red-600", due_soon: "text-amber-600", ok: "text-emerald-600" };

/** Fleet page add-on: pre-trip inspections, service schedules, downtime, cost per truck. */
export function FleetOps({ trucks, onChanged }) {
    const [d, setD] = useState({ inspections: [], schedules: [], downtime: [], costs: [], items: [] });
    const load = () => Promise.all(["inspections", "schedules", "downtime", "costs", "inspection-items"].map((k) => api.get(`/fleet/${k}`)))
        .then(([a, b, c, e, f]) => setD({ inspections: a.data, schedules: b.data, downtime: c.data, costs: e.data, items: f.data }));
    useEffect(() => { load(); }, []);
    const name = (id) => trucks.find((t) => t.id === id)?.name || "—";
    const refresh = () => { load(); onChanged?.(); };

    return (
        <Card data-testid="fleet-ops"><CardContent className="p-5">
            <Tabs defaultValue="inspections">
                <TabsList>
                    <TabsTrigger value="inspections" data-testid="fleet-tab-inspections">Pre-trip inspections</TabsTrigger>
                    <TabsTrigger value="schedules" data-testid="fleet-tab-schedules">Service schedules</TabsTrigger>
                    <TabsTrigger value="downtime" data-testid="fleet-tab-downtime">Downtime</TabsTrigger>
                    <TabsTrigger value="costs" data-testid="fleet-tab-costs">Cost per truck</TabsTrigger>
                </TabsList>
                <TabsContent value="inspections"><Inspections trucks={trucks} items={d.items} rows={d.inspections} name={name} onSaved={refresh} /></TabsContent>
                <TabsContent value="schedules"><Schedules trucks={trucks} rows={d.schedules} onSaved={refresh} /></TabsContent>
                <TabsContent value="downtime"><Downtime trucks={trucks} rows={d.downtime} name={name} onSaved={refresh} /></TabsContent>
                <TabsContent value="costs"><Costs rows={d.costs} /></TabsContent>
            </Tabs>
        </CardContent></Card>
    );
}

function TruckSelect({ trucks, value, onChange, testid }) {
    return (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="w-56" data-testid={testid}><SelectValue placeholder="Truck" /></SelectTrigger>
            <SelectContent>{trucks.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
    );
}

function Inspections({ trucks, items, rows, name, onSaved }) {
    const [truck, setTruck] = useState("");
    const [odo, setOdo] = useState("");
    const [res, setRes] = useState({});
    const [notes, setNotes] = useState("");
    const save = async () => {
        if (!truck || items.some((i) => !res[i])) return toast.error("Pick a truck and mark every item pass or fail");
        try {
            const { data } = await api.post("/fleet/inspections", { truck_id: truck, odometer: odo ? parseInt(odo, 10) : null, results: res, notes });
            toast[data.passed ? "success" : "warning"](data.passed ? "Inspection passed" : "Defects found. Truck marked out of service");
            setRes({}); setNotes(""); onSaved();
        } catch (e) { err(e); }
    };
    return (
        <div className="space-y-4 mt-4">
            <div className="flex flex-wrap gap-2"><TruckSelect trucks={trucks} value={truck} onChange={setTruck} testid="insp-truck" />
                <Input className="w-36" type="number" placeholder="Odometer" value={odo} onChange={(e) => setOdo(e.target.value)} data-testid="insp-odometer" /></div>
            <div className="grid sm:grid-cols-2 gap-2">
                {items.map((it) => (
                    <div key={it} className="flex items-center justify-between border rounded px-3 py-1.5 text-sm">
                        {it}
                        <div className="flex gap-1">{["pass", "fail"].map((v) => (
                            <Button key={v} size="sm" variant={res[it] === v ? (v === "pass" ? "default" : "destructive") : "outline"} className="h-7 text-xs"
                                    onClick={() => setRes({ ...res, [it]: v })} data-testid={`insp-${v}-${it.replace(/\W+/g, "-")}`}>{v}</Button>))}</div>
                    </div>
                ))}
            </div>
            <Input placeholder="Defect notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <Button size="sm" onClick={save} data-testid="insp-submit">Submit inspection</Button>
            <div className="divide-y border rounded text-sm" data-testid="insp-list">
                {rows.slice(0, 20).map((r) => (
                    <div key={r.id} className="px-3 py-2 flex gap-3"><span className="text-xs w-36">{new Date(r.created_at).toLocaleString()}</span>
                        <span className="w-44">{name(r.truck_id)}</span><span className={r.passed ? "text-emerald-600" : "text-red-600"}>{r.passed ? "Passed" : `Failed: ${r.failed_items.join(", ")}`}</span>
                        <span className="text-xs text-muted-foreground ml-auto">{r.driver_email}</span></div>
                ))}
            </div>
        </div>
    );
}

function Schedules({ trucks, rows, onSaved }) {
    const [f, setF] = useState({ truck_id: "", task: "", interval_days: "", interval_miles: "", last_done_date: "", last_done_miles: "" });
    const add = async () => {
        try {
            await api.post("/fleet/schedules", { ...f, interval_days: f.interval_days ? +f.interval_days : null, interval_miles: f.interval_miles ? +f.interval_miles : null,
                last_done_miles: f.last_done_miles ? +f.last_done_miles : null });
            toast.success("Schedule added");
            setF({ truck_id: f.truck_id, task: "", interval_days: "", interval_miles: "", last_done_date: "", last_done_miles: "" });
            onSaved();
        } catch (e) { err(e); }
    };
    const [doneFor, setDoneFor] = useState(null);
    const [cost, setCost] = useState("0");
    const done = (s) => { setCost("0"); setDoneFor(s); };
    const confirmDone = async () => {
        try {
            await api.post(`/fleet/schedules/${doneFor.id}/complete`, { cost: parseFloat(cost) || 0 });
            toast.success("Logged"); setDoneFor(null); onSaved();
        } catch (e) { err(e); }
    };
    return (
        <div className="space-y-4 mt-4">
            <div className="flex flex-wrap gap-2 items-center">
                <TruckSelect trucks={trucks} value={f.truck_id} onChange={(v) => setF({ ...f, truck_id: v })} testid="sched-truck" />
                <Input className="w-40" placeholder="Task (oil change)" value={f.task} onChange={(e) => setF({ ...f, task: e.target.value })} data-testid="sched-task" />
                <Input className="w-28" type="number" placeholder="Every N days" value={f.interval_days} onChange={(e) => setF({ ...f, interval_days: e.target.value })} data-testid="sched-days" />
                <Input className="w-32" type="number" placeholder="Every N miles" value={f.interval_miles} onChange={(e) => setF({ ...f, interval_miles: e.target.value })} data-testid="sched-miles" />
                <Input className="w-40" type="date" value={f.last_done_date} onChange={(e) => setF({ ...f, last_done_date: e.target.value })} title="Last done" />
                <Input className="w-32" type="number" placeholder="Last done miles" value={f.last_done_miles} onChange={(e) => setF({ ...f, last_done_miles: e.target.value })} />
                <Button size="sm" onClick={add} data-testid="sched-add">Add</Button>
            </div>
            <div className="divide-y border rounded text-sm" data-testid="sched-list">
                {rows.map((s) => (
                    <div key={s.id} className="px-3 py-2 flex items-center gap-3"><span className="w-44">{s.truck_name}</span><span className="font-medium w-40">{s.task}</span>
                        <span className={`text-xs w-20 font-semibold ${STATE[s.state]}`}>{s.state.replace("_", " ")}</span>
                        <span className="text-xs text-muted-foreground">{s.due_date && `due ${s.due_date}`} {s.due_miles && `· at ${s.due_miles.toLocaleString()} mi`}</span>
                        <Button size="sm" variant="outline" className="ml-auto" onClick={() => done(s)} data-testid={`sched-done-${s.id}`}>Mark done</Button></div>
                ))}
            </div>
            <Dialog open={!!doneFor} onOpenChange={(o) => !o && setDoneFor(null)}>
                <DialogContent data-testid="sched-done-dialog">
                    <DialogHeader><DialogTitle>Mark "{doneFor?.task}" done today</DialogTitle></DialogHeader>
                    <label className="text-sm">Cost ($)</label>
                    <Input type="number" min="0" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="sched-done-cost" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDoneFor(null)} data-testid="sched-done-cancel">Cancel</Button>
                        <Button onClick={confirmDone} data-testid="sched-done-confirm">Log service</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Downtime({ trucks, rows, name, onSaved }) {
    const [truck, setTruck] = useState("");
    const [reason, setReason] = useState("");
    const add = async () => { try { await api.post("/fleet/downtime", { truck_id: truck, reason }); setReason(""); onSaved(); } catch (e) { err(e); } };
    const end = async (r) => { try { await api.post(`/fleet/downtime/${r.id}/end`); onSaved(); } catch (e) { err(e); } };
    return (
        <div className="space-y-4 mt-4">
            <div className="flex gap-2"><TruckSelect trucks={trucks} value={truck} onChange={setTruck} testid="down-truck" />
                <Input placeholder="Reason / repair needed" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="down-reason" />
                <Button size="sm" onClick={add} disabled={!truck} data-testid="down-add">Take out of service</Button></div>
            <div className="divide-y border rounded text-sm" data-testid="down-list">
                {rows.map((r) => (
                    <div key={r.id} className="px-3 py-2 flex items-center gap-3"><span className="w-44">{name(r.truck_id)}</span><span className="flex-1">{r.reason}</span>
                        <span className="text-xs text-muted-foreground">{new Date(r.start).toLocaleString()} → {r.end ? new Date(r.end).toLocaleString() : "ongoing"}</span>
                        {!r.end && <Button size="sm" variant="outline" onClick={() => end(r)} data-testid={`down-end-${r.id}`}>Back in service</Button>}</div>
                ))}
            </div>
        </div>
    );
}

function Costs({ rows }) {
    return (
        <table className="w-full text-sm mt-4" data-testid="cost-table">
            <thead className="text-xs text-muted-foreground"><tr>{["Truck", "Status", "Repairs", "Preventive", "Fuel & expenses", "Total (12 mo)", "Downtime", "Cost / mile"].map((h) => <th key={h} className="text-left font-medium py-2">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r) => (
                <tr key={r.truck_id} className="border-t"><td className="py-2">{r.truck_name}</td><td className="text-xs">{r.status}</td>
                    <td className="tabular">{fmtMoney(r.repairs)}</td><td className="tabular">{fmtMoney(r.preventative)}</td><td className="tabular">{fmtMoney(r.fuel_and_expenses)}</td>
                    <td className="tabular font-semibold">{fmtMoney(r.total)}</td><td className="text-xs">{r.downtime_hours} h</td>
                    <td className="tabular text-xs">{r.mileage ? `$${(r.total / r.mileage).toFixed(3)}` : "—"}</td></tr>
            ))}</tbody>
        </table>
    );
}
